import React, { useState } from 'react';
import { TabContents } from '../../../2_Molecules/Tabs/TabContents';
import Link from '../../../1_Atoms/Link/Link';
import AccessoryCard from '../../../2_Molecules/AccessoryCard/AccessoryCard';
import { scrollToTop } from '../../../../utils/helpers';
import FiftyFiftyModal from '../../../3_Organisms/FiftyFiftyModal/FiftyFiftyModal';
import { languageController } from '../../../../controllers/language/languageController';
import { LanguageCode } from '../../../../types';
import { AccessoriesData, AccessoryItem } from '../VehicleAccessories';

interface AccessoriesStepProps {
  accessoriesData: AccessoriesData;
  setCurrentStep: (step: number) => void;
  language: LanguageCode;
}

interface TabData {
  id: string;
  tabTitle: string;
  tabContent: React.ReactNode[];
}

interface CategoryData {
  id: string;
  title: string;
}

interface ExpandViewProps {
  name?: string;
  image?: string;
  price?: string;
  description?: string;
}

const AccessoriesStep: React.FC<AccessoriesStepProps> = ({
  accessoriesData,
  setCurrentStep,
  language,
}) => {
  const { accessories, trim } = accessoriesData;
  const [expandViewProps, setExpandViewProps] = useState<ExpandViewProps | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const createTabs = (accessories: AccessoriesData['accessories']): TabData[] | null => {
    if (!accessories) return null;

    const { accessories: accessoriesList, categories } = accessories;
    // Get Popular category id if exists.
    const popularCategoryId = Object.keys(categories).find(key => 
      categories[key].title.toUpperCase() === 'POPULAR' || 
      categories[key].title.toUpperCase() === 'POPULAIRE'
    );

    const categoriesSorted = Object.values(categories)
      .filter(category => category.id !== popularCategoryId)
      .sort((a, b) => a.title.localeCompare(b.title));

    const categoriesAll: CategoryData[] = [
      {
        id: 'all',
        title: languageController.getCopy('vehicleAccessories', 'categoryAll') || ''
      },
      ...categoriesSorted
    ];

    if (popularCategoryId) {
      categoriesAll.unshift({
        id: popularCategoryId,
        title: language === 'en' ? 'Popular' : 'Populaire'
      });
    }

    const tabsContent: (TabData | null)[] = categoriesAll.map(category => {
      const { id: categoryId, title } = category;
      let matchedAccessories: AccessoryItem[] = [];

      for (const key of Object.keys(accessoriesList)) {
        const accId = accessoriesList[key].category?.id;

        if ((title.toUpperCase() === 'POPULAR' || title.toUpperCase() === 'POPULAIRE') && accessoriesList[key].popular) {
          matchedAccessories = [
            ...matchedAccessories,
            accessoriesList[key]
          ];
        }
        else if (accId === categoryId || categoryId === 'all') {
          matchedAccessories = [
            ...matchedAccessories,
            accessoriesList[key]
          ];
        }
      }

      if (!matchedAccessories.length) return null;

      return {
        id: categoryId,
        tabTitle: title,
        tabContent: matchedAccessories.map(acc => {
          return (
            <AccessoryCard
              key={acc.part_number}
              name={acc.name}
              image={acc.image}
              price={parseFloat(acc.price) || 0}
              popular={acc.popular || false}
              description={acc.description}
              setIsModalOpen={setIsModalOpen}
              setExpandViewProps={setExpandViewProps}
            />
          );
        })
      };
    });

    // Return tabsContent items that is not empty
    return tabsContent.filter((item): item is TabData => item !== null);
  };

  const tabsData = createTabs(accessories);

  if (!tabsData || !tabsData.length) {
    return null;
  }

  return (
    <div className="mz-va-accessories-step">
      <div className="mz-container">
        <div className="mz-full-width">
          <div className="mz-va-accessories-step__top">
            <h3 className="text--center">{trim.title} {languageController.getCopy('vehicleAccessories', 'accessories')}</h3>
          </div>
          <div className="mz-va-accessories-step__content">
            <TabContents
              className="mz-bg--off-white"
              dataTabs={tabsData}
              defaultTab={tabsData[0].id}
            />
          </div>
          <div className="mz-va-accessories-step__actions">
            <Link
              text={languageController.getCopy('vehicleAccessories', 'back') || ''}
              onClick={() => {
                // Go back to the init step.
                setCurrentStep(1);
                scrollToTop();
              }}
            />
          </div>
        </div>
      </div>
      {/* Expanded view */}
      <FiftyFiftyModal
        isOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        {...expandViewProps}
      />
    </div>
  );
};

export default AccessoriesStep;
